import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Card, Form, Modal, Button } from 'react-bootstrap'
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table'
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css'
import 'bootstrap/dist/css/bootstrap.min.css'
import { LuGraduationCap } from 'react-icons/lu'
import { BsArrowClockwise, BsFunnelFill, BsSearch } from 'react-icons/bs'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import TablePagination from './commonComponents/TablePagination'
import { useInstitute } from './commonComponents/InstituteContext'
import ApiEndpoints from './commonComponents/ApiEndpoints' 
import { Dropdown } from 'react-bootstrap'
// Add these two imports
import toast, { Toaster } from 'react-hot-toast'
import {
  BsThreeDotsVertical,
  BsEye,
  BsPencilFill,
  BsTrash,
  BsPersonFill} from 'react-icons/bs'

// ── Status & Priority configs (same as MyQueries) ──────────────────────────
const STATUS_CONFIG = {
  open:        { label: 'Open',        bg: '#EFF6FF', color: '#1D4ED8', dot: '#3B82F6' },
  in_progress: { label: 'In Progress', bg: '#FFFBEB', color: '#92400E', dot: '#F59E0B' },
  resolved:    { label: 'Resolved',    bg: '#ECFDF5', color: '#065F46', dot: '#10B981' },
  closed:      { label: 'Closed',      bg: '#F3F4F6', color: '#374151', dot: '#9CA3AF' }}

const PRIORITY_CONFIG = {
  low:      { label: 'Low',      bg: '#F0FDF4', color: '#166534' },
  medium:   { label: 'Medium',   bg: '#EFF6FF', color: '#1E40AF' },
  high:     { label: 'High',     bg: '#FFF7ED', color: '#9A3412' },
  critical: { label: 'Critical', bg: '#FEF2F2', color: '#991B1B' }}

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.open
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      padding: '3px 10px', borderRadius: '20px', fontSize: '11px',
      fontWeight: 700, letterSpacing: '0.3px', background: cfg.bg,
      color: cfg.color, fontFamily: 'Inter, system-ui, sans-serif', whiteSpace: 'nowrap'}}>
      <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
      {cfg.label}
    </span>
  )
}

const PriorityBadge = ({ priority }) => {
  const cfg = PRIORITY_CONFIG[priority?.toLowerCase()] || PRIORITY_CONFIG.medium
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: '20px',
      fontSize: '11px', fontWeight: 700, background: cfg.bg,
      color: cfg.color, fontFamily: 'Inter, system-ui, sans-serif', whiteSpace: 'nowrap'}}>
      {cfg.label}
    </span>
  )
}

const formatDate = (dateStr) => {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric'})
}

// ── View Modal ──────────────────────────────────────────────────────────────
const ViewQueryModal = ({ show, onHide, query }) => (
  <Modal show={show} onHide={onHide} centered size="lg">
    <Modal.Header closeButton style={{ borderBottom: '1px solid #F3F4F6', paddingBottom: '16px' }}>
      <Modal.Title style={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: '18px', color: '#111827' }}>
        Query Details
      </Modal.Title>
    </Modal.Header>
    <Modal.Body style={{ padding: '24px' }}>
      {query && (
        <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
          {/* Guardian Info */}
          <div style={{ background: '#F3F4F6', borderRadius: '10px', padding: '16px', marginBottom: '20px', border: '1px solid #D1D3D4' }}>
            <div className="d-flex align-items-center gap-2 mb-3">
              <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#223843', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <BsPersonFill style={{ color: '#ffffff', fontSize: '16px' }} />
              </div>
              <span style={{ fontWeight: 700, fontSize: '14px', color: '#111827' }}>Guardian Information</span>
            </div>
            <div className="row g-3">
              <div className="col-md-4">
                <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Name</label>
                <p style={{ margin: 0, fontSize: '14px', color: '#111827', fontWeight: 600 }}>{query.guardian_name || '-'}</p>
              </div>
              <div className="col-md-4">
                <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Phone</label>
                <p style={{ margin: 0, fontSize: '14px', color: '#111827', fontWeight: 600 }}>{query.guardian_phone || '-'}</p>
              </div>
              <div className="col-md-4">
                <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Email</label>
                <p style={{ margin: 0, fontSize: '14px', color: '#111827', fontWeight: 600 }}>{query.guardian_email || '-'}</p>
              </div>
            </div>
          </div>

          {/* Query Info */}
          <div className="row g-3 mb-3">
            <div className="col-md-8">
              <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Title</label>
              <p style={{ margin: 0, fontSize: '15px', color: '#111827', fontWeight: 700 }}>{query.title}</p>
            </div>
            <div className="col-md-4">
              <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Category</label>
              <p style={{ margin: 0, fontSize: '14px', color: '#111827' }}>{query.category || '-'}</p>
            </div>
            <div className="col-md-4">
              <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Priority</label>
              <div style={{ marginTop: '4px' }}><PriorityBadge priority={query.priority} /></div>
            </div>
            <div className="col-md-4">
              <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</label>
              <div style={{ marginTop: '4px' }}><StatusBadge status={query.status} /></div>
            </div>
            <div className="col-md-4">
              <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Created</label>
              <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>{formatDate(query.created_at)}</p>
            </div>
            {query.description && (
              <div className="col-12">
                <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Description</label>
                <p style={{ margin: 0, fontSize: '14px', color: '#374151', lineHeight: 1.6, marginTop: '4px', padding: '12px', background: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                  {query.description}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </Modal.Body>
  </Modal>
)

// ── Update Status Modal ─────────────────────────────────────────────────────
const UpdateStatusModal = ({ show, onHide, query, onStatusUpdate }) => {
  const [status, setStatus] = useState(query?.status || 'open')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (query) setStatus(query.status)
    setError('')
  }, [query, show])

// In UpdateStatusModal's handleSave, update the try/catch:
const handleSave = async () => {
  setSaving(true)
  setError('')
  try {
    await ApiEndpoints.updateQuery(query.id, { status })
    toast.success('Status updated successfully')  // ← add this
    onStatusUpdate()
    onHide()
  } catch (err) {
    setError('Failed to update status. Please try again.')
    toast.error('Failed to update status')        // ← add this
    console.error(err.response?.data || err.message)
  } finally {
    setSaving(false)
  }
}

  const statusOptions = [
    { value: 'open',        label: 'Open' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'resolved',    label: 'Resolved' },
    { value: 'closed',      label: 'Closed' },
  ]

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton style={{ borderBottom: '1px solid #F3F4F6' }}>
        <Modal.Title style={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: '18px', color: '#111827' }}>
          Update Query Status
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: '24px', fontFamily: 'Inter, system-ui, sans-serif' }}>
        {query && (
          <>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>Query</p>
            <p style={{ fontSize: '15px', fontWeight: 700, color: '#111827', marginBottom: '20px' }}>{query.title}</p>

            <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px', display: 'block' }}>
              New Status
            </label>
            <div className="d-flex flex-wrap gap-2 mb-3">
              {statusOptions.map((opt) => {
                const cfg = STATUS_CONFIG[opt.value]
                const isActive = status === opt.value
                return (
                  <button
                    key={opt.value}
                    onClick={() => setStatus(opt.value)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '20px',
                      border: isActive ? `2px solid ${cfg.dot}` : '2px solid #D1D3D4',
                      background: isActive ? cfg.bg : '#ffffff',
                      color: isActive ? cfg.color : '#6b7280',
                      fontWeight: isActive ? 700 : 500,
                      fontSize: '13px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      fontFamily: 'Inter, system-ui, sans-serif'}}
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>

            {error && (
              <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', marginBottom: '12px' }}>
                {error}
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer style={{ borderTop: '1px solid #F3F4F6', padding: '16px 24px' }}>
        <button
          onClick={onHide}
          style={{ padding: '9px 20px', borderRadius: '8px', border: '1px solid #D1D3D4', background: '#ffffff', color: '#374151', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, system-ui, sans-serif' }}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{ padding: '9px 20px', borderRadius: '8px', border: 'none', background: '#223843', color: '#ffffff', fontSize: '13px', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, fontFamily: 'Inter, system-ui, sans-serif', marginLeft: '8px' }}
        >
          {saving ? 'Saving...' : 'Update Status'}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

// ── Delete Confirm Modal ────────────────────────────────────────────────────
const DeleteConfirmModal = ({ show, onHide, onConfirm, query, deleting }) => (
  <Modal show={show} onHide={onHide} centered>
    <Modal.Header closeButton style={{ borderBottom: '1px solid #F3F4F6' }}>
      <Modal.Title style={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: '18px', color: '#111827' }}>
        Delete Query
      </Modal.Title>
    </Modal.Header>
    <Modal.Body style={{ padding: '24px', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
        <div style={{ width: '44px', height: '44px', borderRadius: '50%', backgroundColor: '#FEF2F2', border: '1px solid #FECACA', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <BsTrash size={20} style={{ color: '#EF4444' }} />
        </div>
        <div>
          <p style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#111827', marginBottom: '6px' }}>
            Are you sure you want to delete this query?
          </p>
          {query && (
            <p style={{ margin: 0, fontSize: '13px', color: '#6b7280', lineHeight: 1.5 }}>
              <strong style={{ color: '#374151' }}>{query.title}</strong>
              {query.guardian_name && <> — by {query.guardian_name}</>}
            </p>
          )}
          <p style={{ margin: '10px 0 0', fontSize: '13px', color: '#EF4444' }}>
            This action cannot be undone.
          </p>
        </div>
      </div>
    </Modal.Body>
    <Modal.Footer style={{ borderTop: '1px solid #F3F4F6', padding: '16px 24px', gap: '8px' }}>
      <button
        onClick={onHide}
        disabled={deleting}
        style={{ padding: '9px 20px', borderRadius: '8px', border: '1px solid #D1D3D4', background: '#ffffff', color: '#374151', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, system-ui, sans-serif' }}
      >
        Cancel
      </button>
      <button
        onClick={onConfirm}
        disabled={deleting}
        style={{ padding: '9px 20px', borderRadius: '8px', border: 'none', background: '#EF4444', color: '#ffffff', fontSize: '13px', fontWeight: 600, cursor: deleting ? 'not-allowed' : 'pointer', opacity: deleting ? 0.7 : 1, fontFamily: 'Inter, system-ui, sans-serif' }}
      >
        {deleting ? 'Deleting...' : 'Yes, Delete'}
      </button>
    </Modal.Footer>
  </Modal>
)

// ── Main Component ──────────────────────────────────────────────────────────
export default function AdminParentQueryList() {
  const { logout } = useAuth()
  const { isCollapsed } = useSidebar()
  const { logo } = useInstitute()

  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const [showFilterCard, setShowFilterCard] = useState(false)

  const [queries, setQueries] = useState([])
  const [allQueries, setAllQueries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterPriority, setFilterPriority] = useState('')
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)

  const [showViewModal, setShowViewModal] = useState(false)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [selectedQuery, setSelectedQuery] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
const [queryToDelete, setQueryToDelete] = useState(null)
const [deleting, setDeleting] = useState(false)

  // ── Sidebar margin ────────────────────────────────────────────────────────
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`)
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchQueries = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const response = await ApiEndpoints.getMyQueries()
      const data = response.data?.data || []
      setQueries(data)
      setAllQueries(data)
    } catch (err) {
      console.error('Error fetching queries:', err.response?.data || err.message)
      setError('Failed to load queries. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchQueries() }, [fetchQueries])

  // ── Filter & Search ───────────────────────────────────────────────────────
  const filteredQueries = useMemo(() => {
    let result = queries
    if (filterStatus) result = result.filter((q) => q.status === filterStatus)
    if (filterPriority) result = result.filter((q) => q.priority?.toLowerCase() === filterPriority)
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      result = result.filter((item) =>
        Object.values(item).some((val) => val != null && val.toString().toLowerCase().includes(q)),
      )
    }
    return result
  }, [queries, search, filterStatus, filterPriority])

  const totalPages = Math.ceil(filteredQueries.length / itemsPerPage)
  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentQueries = filteredQueries.slice(indexOfFirstItem, indexOfLastItem)

  useEffect(() => {
    if (currentPage > totalPages && totalPages !== 0) setCurrentPage(1)
  }, [filteredQueries, totalPages])

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleRefresh = () => {
    setSearch('')
    setFilterStatus('')
    setFilterPriority('')
    setCurrentPage(1)
    fetchQueries()
  }

  const handleView = (query) => {
    setSelectedQuery(query)
    setShowViewModal(true)
  }

  const handleUpdateStatus = (query) => {
    setSelectedQuery(query)
    setShowStatusModal(true)
  }

  // const handleDelete = async (query) => {
  //   if (!window.confirm('Are you sure you want to delete this query?')) return
  //   try {
  //     await ApiEndpoints.deleteQuery(query.id)
  //     setQueries((prev) => prev.filter((item) => item.id !== query.id))
  //   } catch (err) {
  //     console.error(err.response?.data || err.message)
  //   }
  // }


  // ❌ Remove old handleDelete entirely, replace with these two:

const handleDelete = (query) => {
  setQueryToDelete(query)
  setShowDeleteModal(true)
}

const confirmDelete = async () => {
  setDeleting(true)
  try {
    await ApiEndpoints.deleteQuery(queryToDelete.id)
    setQueries((prev) => prev.filter((item) => item.id !== queryToDelete.id))
    setAllQueries((prev) => prev.filter((item) => item.id !== queryToDelete.id))
    toast.success('Query deleted successfully')
    setShowDeleteModal(false)
    setQueryToDelete(null)
  } catch (err) {
    console.error(err.response?.data || err.message)
    toast.error('Failed to delete query. Please try again.')
  } finally {
    setDeleting(false)
  }
}

  // ── Stats ─────────────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    total: allQueries.length,
    open: allQueries.filter((q) => q.status === 'open').length,
    in_progress: allQueries.filter((q) => q.status === 'in_progress').length,
    resolved: allQueries.filter((q) => q.status === 'resolved').length}), [allQueries])

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        .dropdown-toggle-no-caret::after { display: none !important; }
      `}</style>

      <div style={{
        marginLeft,
        transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        willChange: 'margin-left',
        WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        zIndex: 1,
        marginTop: '0px',
        marginRight: '0px',
        marginBottom: '0px',
        paddingLeft: isCollapsed ? '12px' : '12px',
        paddingRight: isCollapsed ? '12px' : '12px'}}>

        {/* ── Header ── */}
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px'}}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                paddingLeft: '28px',
                paddingRight: '28px',
                paddingTop: '12px',
                paddingBottom: '12px',
                overflow: 'visible',
                borderBottom: '1px solid #D1D3D4',
                width: '100%'}}
            >
              {/* Left: Logo + Title */}
              <div className="d-flex align-items-center gap-3">
                <div style={{
                  width: '40px', height: '40px', borderRadius: '50%', overflow: 'hidden',
                  background: '#223843', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', flexShrink: 0}}>
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>
                <h3
                  className="m-0 fw-bold"
                  style={{
                    letterSpacing: '-0.02em', overflow: 'visible', textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap', fontSize: 'clamp(18px, 2.5vw, 22px)',
                    color: '#223843', lineHeight: '1.2', fontFamily: 'Inter, system-ui, sans-serif'}}
                >
                  Parent Queries
                </h3>
              </div>

              {!isMobile && <ProfileDropdown logout={logout} />}
            </div>
          </div>
        </div>

        {/* ── Body ── */}
        <div
          className="container-fluid p-0 mt-4"
          style={{
            minHeight: '100vh',
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '32px',
            paddingBottom: '32px'}}
        >

          {/* ── Stats Cards ── */}
          <div className="row g-3 mb-4">
            {[
              { label: 'Total Queries', value: stats.total, bg: '#EFF6FF', color: '#1E40AF', dot: '#3B82F6' },
              { label: 'Open',          value: stats.open,       bg: STATUS_CONFIG.open.bg,        color: STATUS_CONFIG.open.color,        dot: STATUS_CONFIG.open.dot },
              { label: 'In Progress',   value: stats.in_progress, bg: STATUS_CONFIG.in_progress.bg, color: STATUS_CONFIG.in_progress.color, dot: STATUS_CONFIG.in_progress.dot },
              { label: 'Resolved',      value: stats.resolved,   bg: STATUS_CONFIG.resolved.bg,    color: STATUS_CONFIG.resolved.color,    dot: STATUS_CONFIG.resolved.dot },
            ].map((s) => (
              <div className="col-6 col-md-3" key={s.label}>
                <div style={{
                  background: s.bg, border: `1px solid ${s.dot}22`, borderRadius: '12px',
                  padding: '16px 20px', fontFamily: 'Inter, system-ui, sans-serif'}}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: s.color, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>{s.label}</div>
                  <div style={{ fontSize: '28px', fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Controls row ── */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center gap-3">
              {/* Items Per Page */}
              <div
                className="d-flex align-items-center"
                style={{
                  backgroundColor: '#ffffff', padding: '8px 16px',
                  borderRadius: '8px', gap: '8px', border: '1px solid #D1D3D4'}}
              >
                <span style={{ color: '#6b7280', fontSize: '13px', whiteSpace: 'nowrap', fontFamily: 'Inter, system-ui, sans-serif' }}>
                  Show:
                </span>
                <Form.Select
                  className="w-auto"
                  value={itemsPerPage}
                  onChange={(e) => { setItemsPerPage(parseInt(e.target.value)); setCurrentPage(1) }}
                  style={{
                    borderRadius: '6px', border: '1px solid #D1D3D4', backgroundColor: '#ffffff',
                    color: '#111827', fontSize: '13px', fontWeight: '500', padding: '4px 32px 4px 12px',
                    minWidth: '140px', cursor: 'pointer', height: '36px', fontFamily: 'Inter, system-ui, sans-serif'}}
                  onFocus={(e) => { e.target.style.borderColor = '#223843' }}
                  onBlur={(e) => { e.target.style.borderColor = '#D1D3D4' }}
                >
                  <option value="10">10 per page</option>
                  <option value="20">20 per page</option>
                  <option value="30">30 per page</option>
                </Form.Select>
              </div>

              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilterCard(!showFilterCard)}
                style={{
                  width: '48px', height: '48px', borderRadius: '50%',
                  backgroundColor: showFilterCard ? '#223843' : '#ffffff',
                  border: '1px solid #D1D3D4', color: showFilterCard ? '#ffffff' : '#223843',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', transition: 'all 0.2s ease'}}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#223843'
                  e.currentTarget.style.color = '#ffffff'
                  e.currentTarget.style.borderColor = '#223843'
                  e.currentTarget.style.transform = 'scale(1.05)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = showFilterCard ? '#223843' : '#ffffff'
                  e.currentTarget.style.color = showFilterCard ? '#ffffff' : '#223843'
                  e.currentTarget.style.borderColor = '#D1D3D4'
                  e.currentTarget.style.transform = 'scale(1)'
                }}
                title={showFilterCard ? 'Hide Filters' : 'Show Filters'}
              >
                <BsFunnelFill size={20} />
              </button>
            </div>
          </div>

          {/* ── Filter Card ── */}
          <div
            style={{
              maxHeight: showFilterCard ? '1000px' : '0',
              overflow: 'hidden',
              transition: 'max-height 0.3s ease-in-out',
              marginBottom: showFilterCard ? '24px' : '0'}}
          >
            {showFilterCard && (
              <Card style={{ border: '1px solid #D1D3D4', borderRadius: '12px', backgroundColor: '#ffffff', overflow: 'hidden' }}>
                {/* Filter Header */}
                <div style={{ background: '#ffffff', padding: '20px', borderBottom: '1px solid #F3F4F6' }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-3">
                      <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#223843', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <BsSearch style={{ color: '#ffffff', fontSize: '18px' }} />
                      </div>
                      <div>
                        <h5 className="m-0 fw-semibold text-start" style={{ color: '#111827', fontSize: '16px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          Search & Filter Queries
                        </h5>
                        <p className="m-0 mt-1" style={{ fontSize: '13px', color: '#6b7280' }}>
                          Filter by status, priority or search keywords
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleRefresh}
                      disabled={loading}
                      style={{
                        width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#223843',
                        border: 'none', color: '#ffffff', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.7 : 1, transition: 'all 0.2s ease'}}
                      onMouseEnter={(e) => { if (!loading) e.currentTarget.style.transform = 'scale(1.05)' }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)' }}
                      title="Refresh"
                    >
                      <BsArrowClockwise size={20} />
                    </button>
                  </div>
                </div>

                {/* Filter Body */}
                <Card.Body className="p-4" style={{ backgroundColor: '#ffffff' }}>
                  <div style={{ padding: '20px', backgroundColor: '#F3F4F6', borderRadius: '8px', border: '1px solid #D1D3D4' }}>
                    <div className="row g-3">
                      {/* Search */}
                      <div className="col-md-6">
                        <label className="fw-semibold mb-2 d-block" style={{ color: '#111827', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          Search Queries
                        </label>
                        <div style={{ position: 'relative' }}>
                          <BsSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280', fontSize: '16px', zIndex: 1 }} />
                          <Form.Control
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by title, guardian, category..."
                            style={{ paddingLeft: '40px', borderRadius: '8px', border: '1px solid #D1D3D4', fontSize: '14px', height: '40px', backgroundColor: '#ffffff', fontFamily: 'Inter, system-ui, sans-serif' }}
                            onFocus={(e) => { e.target.style.borderColor = '#223843' }}
                            onBlur={(e) => { e.target.style.borderColor = '#D1D3D4' }}
                          />
                        </div>
                      </div>

                      {/* Status Filter */}
                      <div className="col-md-3">
                        <label className="fw-semibold mb-2 d-block" style={{ color: '#111827', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          Filter by Status
                        </label>
                        <Form.Select
                          value={filterStatus}
                          onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1) }}
                          style={{ borderRadius: '8px', border: '1px solid #D1D3D4', fontSize: '14px', height: '40px', backgroundColor: '#ffffff', fontFamily: 'Inter, system-ui, sans-serif' }}
                          onFocus={(e) => { e.target.style.borderColor = '#223843' }}
                          onBlur={(e) => { e.target.style.borderColor = '#D1D3D4' }}
                        >
                          <option value="">All Statuses</option>
                          <option value="open">Open</option>
                          <option value="in_progress">In Progress</option>
                          <option value="resolved">Resolved</option>
                          <option value="closed">Closed</option>
                        </Form.Select>
                      </div>

                      {/* Priority Filter */}
                      <div className="col-md-3">
                        <label className="fw-semibold mb-2 d-block" style={{ color: '#111827', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          Filter by Priority
                        </label>
                        <Form.Select
                          value={filterPriority}
                          onChange={(e) => { setFilterPriority(e.target.value); setCurrentPage(1) }}
                          style={{ borderRadius: '8px', border: '1px solid #D1D3D4', fontSize: '14px', height: '40px', backgroundColor: '#ffffff', fontFamily: 'Inter, system-ui, sans-serif' }}
                          onFocus={(e) => { e.target.style.borderColor = '#223843' }}
                          onBlur={(e) => { e.target.style.borderColor = '#D1D3D4' }}
                        >
                          <option value="">All Priorities</option>
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="critical">Critical</option>
                        </Form.Select>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            )}
          </div>

          {/* ── Error ── */}
          {error && (
            <div
              className="alert mb-3"
              style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B', borderRadius: '10px', fontFamily: 'Inter, system-ui, sans-serif', fontSize: '13px' }}
            >
              {error}
            </div>
          )}

          {/* ── Table ── */}
          <Card style={{ border: '1px solid #D1D3D4', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#ffffff' }}>
            <Table className="table mb-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
              <Thead>
                <Tr style={{ backgroundColor: '#F3F4F6' }}>
                  {[ 'Guardian', 'Title', 'Category', 'Priority', 'Status', 'Created At', 'Action'].map((h) => (
                    <Th
                      key={h}
                      style={{
                        fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none',
                        borderBottom: '1px solid #D1D3D4', textAlign: 'left',
                        color: '#111827', fontFamily: 'Inter, system-ui, sans-serif'}}
                    >
                      {h}
                    </Th>
                  ))}
                </Tr>
              </Thead>

              <Tbody>
                {loading ? (
                  <Tr>
                    <Td colSpan="8" style={{ textAlign: 'center', padding: '40px', fontSize: '14px', color: '#6b7280', fontFamily: 'Inter, system-ui, sans-serif' }}>
                      <div className="spinner-border spinner-border-sm me-2" role="status" style={{ color: '#223843' }} />
                      Loading queries...
                    </Td>
                  </Tr>
                ) : currentQueries.length > 0 ? (
                  currentQueries.map((query, index) => (
                    <Tr
                      key={query.id}
                      style={{
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#F3F4F6',
                        transition: 'background-color 0.2s ease',
                        borderBottom: '1px solid #D1D3D4'}}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F3F4F6' }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#F3F4F6' }}
                    >
                      {/* ID */}
                      {/* <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#6b7280', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, textAlign: 'left' }}>
                        #{query.id}
                      </Td> */}

                      {/* Guardian */}
                      <Td style={{ fontSize: '14px', padding: '16px', border: 'none', textAlign: 'left' }}>
                        <div style={{ fontWeight: 700, color: '#111827', fontFamily: 'Inter, system-ui, sans-serif', fontSize: '13px' }}>
                          {query.guardian_name || '-'}
                        </div>
                        {query.guardian_phone && (
                          <div style={{ fontSize: '12px', color: '#6b7280', fontFamily: 'Inter, system-ui, sans-serif', marginTop: '2px' }}>
                            {query.guardian_phone}
                          </div>
                        )}
                      </Td>

                      {/* Title */}
                      <Td
                        style={{
                          fontSize: '14px', padding: '16px', border: 'none',
                          color: '#111827', fontFamily: 'Inter, system-ui, sans-serif',
                          fontWeight: 600, maxWidth: '220px', overflow: 'hidden',
                          textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left'}}
                        title={query.title}
                      >
                        {query.title}
                      </Td>

                      {/* Category */}
                      <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif', textAlign: 'left' }}>
                        {query.category || '-'}
                      </Td>

                      {/* Priority */}
                      <Td style={{ fontSize: '14px', padding: '16px', border: 'none', textAlign: 'left' }}>
                        <PriorityBadge priority={query.priority} />
                      </Td>

                      {/* Status */}
                      <Td style={{ fontSize: '14px', padding: '16px', border: 'none', textAlign: 'left' }}>
                        <StatusBadge status={query.status} />
                      </Td>

                      {/* Created */}
                      <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#6b7280', fontFamily: 'Inter, system-ui, sans-serif', textAlign: 'left' }}>
                        {formatDate(query.created_at)}
                      </Td>

                      {/* Actions */}
                      <Td className="text-center" style={{ padding: '16px', border: 'none' }}>
                        {/* Desktop Dropdown */}
                        <div className="d-none d-md-block">
                          <Dropdown>
                            <Dropdown.Toggle
                              variant="light"
                              className="dropdown-toggle-no-caret"
                              style={{
                                width: '36px', height: '36px', borderRadius: '50%',
                                backgroundColor: 'transparent', border: '1px solid #D1D3D4',
                                padding: '0', display: 'flex', alignItems: 'center',
                                justifyContent: 'center', transition: 'all 0.2s ease'}}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#223843'
                                e.currentTarget.style.borderColor = '#223843'
                                e.currentTarget.style.color = '#ffffff'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent'
                                e.currentTarget.style.borderColor = '#D1D3D4'
                                e.currentTarget.style.color = '#111827'
                              }}
                            >
                              <BsThreeDotsVertical style={{ color: 'inherit' }} />
                            </Dropdown.Toggle>

                            <Dropdown.Menu style={{ borderRadius: '8px', border: '1px solid #D1D3D4', padding: '8px', minWidth: '190px' }}>
                              {/* View */}
                              <Dropdown.Item
                                onClick={() => handleView(query)}
                                style={{ fontSize: '13px', padding: '10px 12px', color: '#111827', display: 'flex', alignItems: 'center', gap: '10px', borderRadius: '6px', transition: 'all 0.2s ease', fontFamily: 'Inter, system-ui, sans-serif' }}
                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#EFF6FF'; e.currentTarget.style.color = '#1D4ED8' }}
                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#111827' }}
                              >
                                <BsEye size={16} /> View Details
                              </Dropdown.Item>

                              {/* Update Status */}
                              <Dropdown.Item
                                onClick={() => handleUpdateStatus(query)}
                                style={{ fontSize: '13px', padding: '10px 12px', color: '#111827', display: 'flex', alignItems: 'center', gap: '10px', borderRadius: '6px', transition: 'all 0.2s ease', fontFamily: 'Inter, system-ui, sans-serif' }}
                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#FFFBEB'; e.currentTarget.style.color = '#92400E' }}
                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#111827' }}
                              >
                                <BsPencilFill size={16} /> Update Status
                              </Dropdown.Item>

                              <Dropdown.Divider style={{ margin: '4px 0' }} />

                              {/* Delete */}
                              <Dropdown.Item
                                onClick={() => handleDelete(query)}
                                style={{ fontSize: '13px', padding: '10px 12px', color: '#dc3545', display: 'flex', alignItems: 'center', gap: '10px', borderRadius: '6px', transition: 'all 0.2s ease', fontFamily: 'Inter, system-ui, sans-serif' }}
                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#FEE2E2'; e.currentTarget.style.color = '#991B1B' }}
                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#dc3545' }}
                              >
                                <BsTrash size={16} /> Delete Query
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>

                        {/* Mobile Buttons */}
                        <div className="d-md-none d-flex justify-content-start align-items-center gap-2">
                          <button
                            onClick={() => handleView(query)}
                            style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#3B82F6', border: 'none', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s ease' }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1D4ED8'; e.currentTarget.style.transform = 'scale(1.1)' }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#3B82F6'; e.currentTarget.style.transform = 'scale(1)' }}
                            title="View Details"
                          >
                            <BsEye size={16} />
                          </button>

                          <button
                            onClick={() => handleUpdateStatus(query)}
                            style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#F59E0B', border: 'none', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s ease' }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#D97706'; e.currentTarget.style.transform = 'scale(1.1)' }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#F59E0B'; e.currentTarget.style.transform = 'scale(1)' }}
                            title="Update Status"
                          >
                            <BsPencilFill size={16} />
                          </button>

                          <button
                            onClick={() => handleDelete(query)}
                            style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#EF4444', border: 'none', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s ease' }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#DC2626'; e.currentTarget.style.transform = 'scale(1.1)' }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#EF4444'; e.currentTarget.style.transform = 'scale(1)' }}
                            title="Delete Query"
                          >
                            <BsTrash size={16} />
                          </button>
                        </div>
                      </Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td
                      colSpan="8"
                      className="text-center"
                      style={{ padding: '40px', fontSize: '14px', color: '#6b7280', fontFamily: 'Inter, system-ui, sans-serif' }}
                    >
                      {search || filterStatus || filterPriority ? 'No queries match your filters.' : 'No queries found.'}
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Card>

          <TablePagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={totalPages}
            indexOfFirstItem={indexOfFirstItem}
            indexOfLastItem={indexOfLastItem}
            totalItems={filteredQueries.length}
            itemLabel="queries"
          />

        </div>
      </div>

      {/* ── Modals ── */}
      {/* <ViewQueryModal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        query={selectedQuery}
      /> */}

      {/* <UpdateStatusModal
        show={showStatusModal}
        onHide={() => setShowStatusModal(false)}
        query={selectedQuery}
        onStatusUpdate={fetchQueries}
      /> */}
      {/* Replace your closing modals section with this: */}
      <ViewQueryModal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        query={selectedQuery}
      />

      <UpdateStatusModal
        show={showStatusModal}
        onHide={() => setShowStatusModal(false)}
        query={selectedQuery}
        onStatusUpdate={fetchQueries}
      />

      {/* ── NEW: Delete Confirm Modal ── */}
      <DeleteConfirmModal
        show={showDeleteModal}
        onHide={() => { setShowDeleteModal(false); setQueryToDelete(null) }}
        onConfirm={confirmDelete}
        query={queryToDelete}
        deleting={deleting}
      />

      {/* ── NEW: Toast Container ── */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSize: '14px',
            fontWeight: 600,
            borderRadius: '10px',
            padding: '12px 16px'},
          success: {
            style: {
              background: '#ECFDF5',
              color: '#065F46',
              border: '1px solid #6EE7B7'},
            iconTheme: { primary: '#10B981', secondary: '#ffffff' }},
          error: {
            style: {
              background: '#FEF2F2',
              color: '#991B1B',
              border: '1px solid #FECACA'},
            iconTheme: { primary: '#EF4444', secondary: '#ffffff' }}}}
      />
    </>
  )
}